import React from 'react';

const STORAGE_KEY = 'chunk_reload_attempted';

/**
 * Heuristic to detect chunk/dynamic-import load failures vs other errors.
 * Vite and Webpack produce different messages, so we check for known patterns.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    // Vite
    msg.includes('failed to fetch dynamically imported module') ||
    // Webpack
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    // Generic network failures during dynamic import
    msg.includes('dynamically imported module') ||
    // Chrome/Safari network error on script fetch
    (error.name === 'TypeError' && msg.includes('failed to fetch'))
  );
}

/**
 * Wraps React.lazy() to handle chunk load failures caused by new deployments.
 *
 * When the app is updated while a user has an active session, the old chunk
 * filenames (e.g., Dialog-abc123.js) no longer exist on the server. Navigating
 * to a lazy-loaded route then fails with a ChunkLoadError / dynamic import error.
 *
 * This wrapper catches that error and performs a single hard reload so the browser
 * fetches the new index.html (which references the new chunk filenames). A
 * sessionStorage flag prevents infinite reload loops.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    importFn()
      .then((module) => {
        // Import succeeded — clear any previous reload flag so future
        // deployments can still trigger the one-time reload.
        sessionStorage.removeItem(STORAGE_KEY);
        return module;
      })
      .catch((error: unknown) => {
        // Only attempt a reload for chunk/network errors, not for syntax
        // errors, auth failures, etc.
        if (!isChunkLoadError(error)) {
          throw error;
        }

        const alreadyReloaded = sessionStorage.getItem(STORAGE_KEY);

        if (!alreadyReloaded) {
          sessionStorage.setItem(STORAGE_KEY, 'true');
          console.warn('[lazyWithRetry] Chunk load failed, reloading page to fetch new assets.', error);
          window.location.reload();

          // Return a never-resolving promise so React doesn't try to render
          // while the page is reloading.
          return new Promise<{ default: T }>(() => {});
        }

        // Already tried reloading once — clear the flag and let the error
        // propagate so the ErrorBoundary can show a proper fallback.
        sessionStorage.removeItem(STORAGE_KEY);
        throw error;
      })
  );
}

/**
 * Same as lazyWithRetry but for modules where you need to pick a named export.
 *
 * Usage:
 *   lazyWithRetryNamed(() => import('./Foo'), m => m.Foo)
 */
export function lazyWithRetryNamed<
  M extends Record<string, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends React.ComponentType<any>
>(importFn: () => Promise<M>, pick: (mod: M) => T): React.LazyExoticComponent<T> {
  return lazyWithRetry(() => importFn().then((mod) => ({ default: pick(mod) })));
}
